import { TERMINAL_RUN_STATUSES } from '@af/shared';
import { call } from './api';

/** A run that has not finished yet (queued, running, waiting for a person or scheduled). */
export const isActiveRun = (status: string) =>
  !TERMINAL_RUN_STATUSES.has(status as Parameters<typeof TERMINAL_RUN_STATUSES.has>[0]);

/** Waiting for a person: an approval or an `ask_human` answer. */
export const needsPerson = (status: string) => status === 'awaiting_approval' || status === 'awaiting_human';

export interface BulkResult {
  stopped: number;
  /** Already finished by the time the request arrived (409). */
  alreadyDone: number;
  failed: number;
}

/**
 * Cancel runs through the normal audited `cancelRun` endpoint, a few at a time. A run that finished in
 * the meantime counts as `alreadyDone`, not as a failure.
 */
export async function cancelRuns(
  ids: string[],
  opts: { concurrency?: number; cancel?: (id: string) => Promise<unknown> } = {},
): Promise<BulkResult> {
  const cancel = opts.cancel ?? ((id: string) => call('cancelRun', { params: { id } }));
  const result: BulkResult = { stopped: 0, alreadyDone: 0, failed: 0 };
  const queue = [...ids];
  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      try {
        await cancel(id);
        result.stopped++;
      } catch (e) {
        if ((e as { status?: number }).status === 409) result.alreadyDone++;
        else result.failed++;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(opts.concurrency ?? 4, ids.length) }, worker));
  return result;
}
